import { faker } from "@msuite/mocking/faker";
import { ChartHeader } from "@msuite/ui/charts";
import { DashboardTile } from "@msuite/ui/dashboard";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import { cn } from "@msuite/ui/utils";
import { array } from "@msuite/utils/array";
import { randomBoolean } from "@msuite/utils/boolean";
import { getColorByString } from "@msuite/utils/colors";
import { useState } from "react";
import type { DashboardTileProps } from "../shared";

const _items = array(20).map((id) => ({
	id,
	content: faker.lorem.words(3),
	type: faker.lorem.words(1),
	isDone: randomBoolean(0.1),
}));

function sortItems(i: typeof _items) {
	return i
		.toSorted((a, b) => {
			return a.content.localeCompare(b.content) ? 1 : -1;
		})
		.toSorted((a) => (a.isDone ? -1 : 1));
}

export function DashboardTasksItem({ item }: DashboardTileProps) {
	/** Hooks */
	const { t } = useTranslation();
	const [items, setItems] = useState(sortItems(_items));

	function handleOnToggleDone(item: (typeof _items)[number]) {
		const itemIndex = items.findIndex((i) => i.id === item.id);
		if (itemIndex < 0) return;
		const newItems = [...items];
		if (!newItems[itemIndex]) return;
		newItems[itemIndex].isDone = !newItems[itemIndex].isDone;
		setItems(sortItems(newItems));
	}

	/** Render */
	return (
		<DashboardTile item={item}>
			<ChartHeader
				title={t("dashboard.my_tasks")}
				description={t("tasks.n_tasks", { count: `${20}` })}
			/>
			<DashboardTile.List
				isLoading={false}
				data={items}
				queryKey={[]}
			>
				{items.map((item) => (
					<DashboardTile.List.Item key={item.id}>
						<button
							type="button"
							className={cn(
								"p-1 aspect-square rounded-full border border-accent-500 bg-accent-500/10 grayscale cursor-pointer mr-2",
								"hover:grayscale-0",
								item.isDone && "grayscale-0",
							)}
							onClick={() => handleOnToggleDone(item)}
						>
							<Icon
								name="Check"
								size={8}
								className="stroke-accent-500"
							/>
						</button>
						<p className="tabular-nums capitalize">{item.content}</p>
						<div className="flex-1" />
						<Tag
							variant="plain"
							color={getColorByString(item.type)}
							className="capitalize"
						>
							{item.type}
						</Tag>
					</DashboardTile.List.Item>
				))}
			</DashboardTile.List>
		</DashboardTile>
	);
}
